import { supabase } from '@/lib/supabase';

export type PublicProfessional = {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  business_logo_url: string | null;
  use_logo_as_avatar: boolean | null;
  business_name: string | null;
  show_business_name: boolean | null;
  specialty: string | null;
  location_id: number | null;
  location_name: string | null;
  region: string | null;
  profession_category_id: number | null;
  profession_category_name: string | null;
  verified: boolean | null;
  current_level: number | null;
  avg_rating: number | null;
  completed_count: number | null;
  is_sponsored: boolean | null;
};

export type SponsoredTier = 'regional' | 'nationwide';

export type MySponsoredListing = {
  tier: SponsoredTier;
  region: string | null;
  daily_rate: number;
  active: boolean;
  last_charged_date: string | null;
};

/** Ελάχιστη επιτρεπτή ημερήσια τιμή ανά tier — ο επαγγελματίας μπορεί να
 * πληρώσει και παραπάνω, για να ξεπεράσει στην κατάταξη κάποιον άλλο. */
export const SPONSORED_INK_PER_DAY: Record<SponsoredTier, number> = {
  regional: 3,
  nationwide: 10,
};

/** Ενεργοποίηση/ενημέρωση συνδρομής προώθησης — καμία άμεση χρέωση, η πρώτη
 * χρέωση γίνεται το επόμενο βράδυ (νυχτερινή διαδικασία). */
export async function startOrUpdateSponsorship(tier: SponsoredTier, dailyRate: number): Promise<{ tier: SponsoredTier; daily_rate: number }> {
  const { data, error } = await supabase.rpc('start_or_update_sponsorship' as never, { p_tier: tier, p_daily_rate: dailyRate } as never);
  if (error) throw error;
  return data as { tier: SponsoredTier; daily_rate: number };
}

/** Ακύρωση ενεργής συνδρομής προώθησης — καμία επόμενη χρέωση. */
export async function cancelSponsorship(): Promise<void> {
  const { error } = await supabase.rpc('cancel_sponsorship' as never);
  if (error) throw error;
}

/** Η δική μου τρέχουσα ενεργή προώθηση, αν υπάρχει. */
export async function getMySponsoredListing(): Promise<MySponsoredListing | null> {
  const { data, error } = await supabase.rpc('get_my_sponsored_listing' as never);
  if (error) throw error;
  const row = (data ?? [])[0] as MySponsoredListing | undefined;
  return row ?? null;
}

/** Η τρέχουσα κορυφαία ημερήσια τιμή μεταξύ όσων θα εμφανίζονταν μαζί μου
 * στον νομό μου (nationwide sponsors παντού + regional του ίδιου νομού),
 * εξαιρώντας τη δική μου συνδρομή — για το "ξεπέρασε τον πρώτο" στο UI. */
export async function getSponsorTopRate(region: string): Promise<number | null> {
  const { data, error } = await supabase.rpc('get_sponsor_top_rate' as never, { p_region: region } as never);
  if (error) throw error;
  return (data as number | null) ?? null;
}

export type SponsoredBannerEntry = {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  business_logo_url: string | null;
  use_logo_as_avatar: boolean | null;
  business_name: string | null;
  show_business_name: boolean | null;
  specialty: string | null;
  profession_category_name: string | null;
  avg_rating: number | null;
};

/** Επαγγελματίες με nationwide προώθηση (20 μελάνια/ημέρα) που έχουν τον ίδιο
 * νομό με αυτόν που βλέπει ο χρήστης — για το κυλιόμενο banner στη λίστα. */
export async function listNationwideSponsoredForRegion(region: string): Promise<SponsoredBannerEntry[]> {
  const { data, error } = await supabase.rpc('list_nationwide_sponsored_for_region' as never, { p_region: region } as never);
  if (error) throw error;
  return (data ?? []) as SponsoredBannerEntry[];
}

export type ProfessionalFacet = 'services' | 'eshop' | 'physical_only';

export async function listPublicProfessionals(
  region: string | null,
  professionCategoryId: number | null,
  facet: ProfessionalFacet | null = null,
): Promise<PublicProfessional[]> {
  const { data, error } = await supabase.rpc('list_public_professionals' as never, {
    p_region: region,
    p_profession_category_id: professionCategoryId,
    p_facet: facet,
  } as never);
  if (error) throw error;
  return (data ?? []) as PublicProfessional[];
}

/** Μόνο οι νομοί που χρησιμοποιεί ήδη τουλάχιστον ένας δημόσιος επαγγελματίας. */
export async function listUsedProfessionalRegions(): Promise<string[]> {
  const { data, error } = await supabase.rpc('list_used_professional_regions' as never);
  if (error) throw error;
  return ((data ?? []) as { region: string }[]).map((r) => r.region);
}

/** Μόνο οι κατηγορίες επαγγέλματος που χρησιμοποιεί ήδη τουλάχιστον ένας δημόσιος επαγγελματίας. */
export async function listUsedProfessionCategories(): Promise<{ id: number; name: string }[]> {
  const { data, error } = await supabase.rpc('list_used_profession_categories' as never);
  if (error) throw error;
  return (data ?? []) as { id: number; name: string }[];
}

/** Αριθμός δημόσιων επαγγελματιών ανά νομό — για τον διαδραστικό χάρτη. */
export async function getProfessionalCountsByRegion(
  facet: ProfessionalFacet | null = null,
  professionCategoryId: number | null = null,
): Promise<Record<string, number>> {
  const { data, error } = await supabase.rpc('get_professional_counts_by_region' as never, {
    p_facet: facet,
    p_profession_category_id: professionCategoryId,
  } as never);
  if (error) throw error;
  const map: Record<string, number> = {};
  for (const row of (data ?? []) as { region: string; count: number }[]) map[row.region] = row.count;
  return map;
}
